// @flow

import * as React from 'react';

import LegacyDispatcher from "app/lib/legacyDispatcher";

import PipelineForm from './Form';
import StepPreview from './StepPreview';
import Icon from 'app/components/shared/Icon';
import Dropdown from 'app/components/shared/Dropdown';
import Button from 'app/components/shared/Button';

import ProjectPipelineStore, { type StepID, type StepReorderPosition, type StepPartial, type Step, type Agent } from 'app/stores/ProjectPipelineStore';

type Props = {
  store: ProjectPipelineStore,
  accountID: string,
  legacyAgentHidden: boolean
};

type State = {
  steps: Array<Step>,
  agents: Array<Agent>
};

/* eslint-disable react/require-optimization */
// TODO: Fix the architecture of these components so this one can be optimised.
// The problem is the `step` object is actually passed from the store, which is always
// the same object reference, even if mutated, so ShallowCompare functions do not work.

export default class Pipeline extends React.Component<Props, State> {
  dropdownNode: ?Dropdown;

  constructor(props: Props) {
    super(props);

    this.state = {
      steps: this.props.store.orderedSteps(),
      agents: this.props.store.orderedAgents()
    };
  }

  isPipelineUploadStep(step: Step) {
    return (
      step.command &&
      (step.command.indexOf("buildkite-agent pipeline") !== -1)
    );
  }

  componentDidMount() {
    this.props.store.on('change', this.handleStoreChange);
  }

  componentWillUnmount() {
    this.props.store.off('change', this.handleStoreChange);
  }

  nonDeletedSteps(): Array<Step> {
    // We don't want to pass destroyed steps to the preview components. We DO want to pass destroyed
    // steps to the forms though (we want the form fields to be present when submitting)
    return this.state.steps.filter((step) => !step.destroyed);
  }

  render() {
    return (
      <div className="ProjectPipelineComponent">
        <div className="ProjectPipelineComponent__steps-panel">
          <div className="flex flex-wrap">
            {this.renderStepPreviews()}
            {this.renderAddStepButton()}
          </div>
        </div>
        {this.state.steps.map((step, index) => {
          return (
            <PipelineForm
              key={step.id}
              step={step}
              accountID={this.props.accountID}
              agents={this.state.agents}
              index={index}
              legacyAgentHidden={this.props.legacyAgentHidden}
              autofocus={index !== 0}
            />
          );
        })}
      </div>
    );
  }

  renderStepPreviews(): Array<React.Element<any>> {
    return this.nonDeletedSteps().map((step) => (
      <StepPreview
        key={step.id}
        step={step}
        onSort={this.handleSort}
        onClick={this.handleStepPreviewClick}
      />
    ));
  }

  renderAddStepButton() {
    return (
      <Dropdown width={250} className="inline-block" ref={(dropdownNode) => this.dropdownNode = dropdownNode}>
        <Button type="button" style={{ height: 35, lineHeight: "12px" }}>
          Add
          <Icon
            icon="down-triangle"
            className="flex-none"
            style={{
              width: 7,
              height: 7,
              marginLeft: '.5em'
            }}
          />
        </Button>

        <div>
          <a href="#" onClick={this.handleNewScriptClick} className="btn black hover-lime focus-lime flex items-center flex-none semi-bold block py2">
            Run script or command
          </a>
          <a href="#" onClick={this.handleNewWaiterClick} className="btn black hover-lime focus-lime flex items-center flex-none semi-bold block py2">
            Wait for previous steps to pass
          </a>
          <a href="#" onClick={this.handleNewManualClick} className="btn black hover-lime focus-lime flex items-center flex-none semi-bold block py2">
            Wait and block pipeline
          </a>
          <hr className="my1" />
          <a href="#" onClick={this.handleNewPipelineUploadClick} className="btn black hover-lime focus-lime flex items-center flex-none semi-bold block py2">
            Read steps from repository
          </a>
        </div>
      </Dropdown>
    );
  }

  deleteEmptyStep() {
    const steps = this.nonDeletedSteps();

    if ((steps.length === 1) && this.isEmptyStep(steps[0])) {
      return LegacyDispatcher.emit("project_step:destroy", { step: steps[0] });
    }
  }

  isEmptyStep(step: Step) {
    return (
      !step.persisted &&
      !step.name &&
      !step.command &&
      step.agentQueryRules &&
      (
        (step.agentQueryRules.length === 0) ||
        (
          (step.agentQueryRules.length === 1) &&
          (step.agentQueryRules[0] === "")
        )
      )
    );
  }

  // Triggered when the store changes
  handleStoreChange = () => {
    this.setState({
      steps: this.props.store.orderedSteps(),
      agents: this.props.store.orderedAgents()
    });
  };

  handleNewScriptClick = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    if (this.dropdownNode) {
      this.dropdownNode.setShowing(false);
    }

    return this.emitNewStepEvent({
      type: 'script'
    });
  };

  handleNewWaiterClick = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    if (this.dropdownNode) {
      this.dropdownNode.setShowing(false);
    }

    return this.emitNewStepEvent({
      type: 'waiter'
    });
  };

  handleNewManualClick = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    if (this.dropdownNode) {
      this.dropdownNode.setShowing(false);
    }

    return this.emitNewStepEvent({
      type: 'manual'
    });
  };

  handleNewPipelineUploadClick = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    if (this.dropdownNode) {
      this.dropdownNode.setShowing(false);
    }
    evt.preventDefault();
    this.deleteEmptyStep();

    return this.emitNewStepEvent({
      type: 'script',
      command: 'buildkite-agent pipeline upload',
      name: ':pipeline:'
    });
  };

  emitNewStepEvent(stepAttributes: StepPartial) {
    return LegacyDispatcher.emit("project_step:new", { attributes: stepAttributes });
  }

  handleStepPreviewClick = (step: Step) => {
    return LegacyDispatcher.emit("project_step:select", { step });
  }

  handleSort = (stepId: StepID, targetId: StepID, position: ?StepReorderPosition) => {
    return LegacyDispatcher.emit("project_step:sort", { stepId, targetId, position });
  }
}
